import React, {useContext, useEffect, useState} from 'react';
import {CircularProgress} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import JString from '@easytech-international-sdn-bhd/jstring';
import FeedbackIcon from '@material-ui/icons/Feedback';
import MyLocationIcon from '@material-ui/icons/MyLocation';
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera';
import PhotoLibraryIcon from '@material-ui/icons/PhotoLibrary';
import ScheduleIcon from '@material-ui/icons/Schedule';
import StoreIcon from '@material-ui/icons/Store';
import EventNoteIcon from '@material-ui/icons/EventNote';
import AppContext from '../../../../Services/AppContext';
import PersistantData from '../../../../Services/PersistantData';
import {axiosDefaultConfig, axiosPutConfig} from '../../../../Services/API_Service';
import {isPromoter} from '../../../../Helper/Helper';
import {resolvePhotoVersion} from '../../../../Helper/SystemSettings';
import SettingsScaffold from './SettingsScaffold';
import OutletCodeSection from './OutletCodeSection';
import PhotoTypeV1Section from './PhotoTypeV1Section';
import PhotoTypeV2Section from './PhotoTypeV2Section';
import CheckInSection from './CheckInSection';
import FeedbackTitleSection from './FeedbackTitleSection';
import PromoterCategorySection from './PromoterCategorySection';
import WorkingShiftSection from './WorkingShiftSection';
import LeaveTypeSection from './LeaveTypeSection';

const useStyles = makeStyles(() => ({
    loader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 160px)'
    }
}));

export default function SystemSettingsPage() {
    const classes = useStyles();
    const [gState] = useContext(AppContext);
    const singleton = PersistantData.getInstance();
    const language = typeof gState.translation === 'undefined' ? singleton.getlang() : gState.translation;
    const [axiosConfig, setAxiosConfig] = useState(null);
    const [axiosPut, setAxiosPut] = useState(null);
    const promoter = isPromoter(gState.position);
    const photoVersion = resolvePhotoVersion(gState.photoGalleryConfigLoaded, gState.defaultPhotoGallery);
    const modules = gState.authorizedModules || {};

    useEffect(() => {
        if (JString(gState.client).isValid()) {
            setAxiosConfig(axiosDefaultConfig(gState.client));
            setAxiosPut(axiosPutConfig(gState.client));
        }
    }, [gState.client]);

    if (photoVersion === null) {
        return <div className={classes.loader} role="status" aria-label={language?.loading || 'Loading'}><CircularProgress/></div>;
    }

    if (!axiosConfig || !axiosPut || !language) return null;

    const sharedProps = {axiosConfig, axiosPut, language};
    const sections = [
        {
            key: 'outlet',
            label: language.outlet_code,
            description: language.outlet_code_desc || 'Outlet code auto-generation rules (read-only).',
            icon: <StoreIcon/>,
            content: <OutletCodeSection axiosConfig={axiosConfig} language={language}/>
        },
        {
            key: 'shift',
            label: language.working_shift,
            description: language.working_shift_desc || 'Define working shifts used by scheduling.',
            icon: <ScheduleIcon/>,
            content: <WorkingShiftSection {...sharedProps}/>
        },
        {
            key: 'checkin',
            label: language.checkin_settings,
            description: language.checkin_settings_subtitle,
            icon: <MyLocationIcon/>,
            content: <CheckInSection axiosConfig={axiosConfig} language={language}/>
        },
        {
            key: 'photoType',
            label: language.photo_type,
            description: photoVersion === 2
                ? (language.photo_type_v2_desc || 'Organize photo groups and types, then publish their display order.')
                : (language.photo_type_desc || 'Manage photo categories and publish their display order.'),
            icon: <PhotoLibraryIcon/>,
            content: photoVersion === 2 ? <PhotoTypeV2Section {...sharedProps}/> : <PhotoTypeV1Section {...sharedProps}/>
        },
        {
            key: 'promoter',
            label: language.promoter_photo_categories,
            description: language.promoter_photo_categories_desc || 'Manage photo categories available to promoters.',
            icon: <PhotoCameraIcon/>,
            visible: promoter,
            content: promoter ? <PromoterCategorySection {...sharedProps}/> : null
        },
        {
            key: 'feedback',
            label: language.feedback_title,
            description: language.feedback_title_desc || 'Manage the title options used by the feedback form.',
            icon: <FeedbackIcon/>,
            content: <FeedbackTitleSection {...sharedProps}/>
        },
        {
            key: 'leaveType',
            label: language.leave_type,
            description: language.leave_type_desc,
            icon: <EventNoteIcon/>,
            visible: !!modules.leaves,
            content: modules.leaves ? <LeaveTypeSection {...sharedProps}/> : null
        }
    ];

    return <SettingsScaffold language={language} sections={sections}/>;
}
